import { useI18n } from '@ecom/i18n';
import { Badge, type BadgeTone, Card } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode } from 'react';

import { TrendGlyph } from './TrendGlyph';

import { CountUp } from '@/components/ui/CountUp';

/**
 * A KPI whose backend does not exist yet renders an honest "Bientôt" state,
 * never a fabricated number — the same UI-honesty rule already established
 * for the admin dashboard's plan/commission figures
 * (IMPLEMENTATION_MASTER_PLAN.md §7.4): "any screen whose data depends on an
 * unresolved decision renders an inline state — never fabricated numbers."
 * Applied here to Orders/Payments, which have no backend at all yet
 * (Sprints 13+ in the plan), not only to a business-model arbitration.
 *
 * Sprint UX/UI Merchant Dashboard V2 — extended with an optional icon
 * chip, semantic `tone`, and `trend` line (all four Dashboard KPIs now
 * carry real data — see DashboardPage.tsx). Every new prop is optional so
 * `AnalyticsPage.tsx`'s existing plain usage, and any still-genuine
 * "Bientôt" card, keep rendering unchanged.
 *
 * Tone-to-class pairs are a static, fully-literal lookup (`TONE_CLASSES`)
 * rather than a template string (`` `bg-${tone}Soft}` ``) — Tailwind's JIT
 * content scanner only picks up class names it can see verbatim in source,
 * so a templated string would silently produce unstyled chips in production.
 */
export type KpiTone = BadgeTone;

/**
 * The icon tile per kind of figure, as the Figma design (« Souk 2.0 ») draws it in the corner of each card: a tint of
 * the kind's colour with the glyph in that colour (sage, mint, amber…).
 */
const TONE_CLASSES: Record<KpiTone, string> = {
  accent: 'sk-kpi-icon is-sage',
  good: 'sk-kpi-icon is-mint',
  warn: 'sk-kpi-icon is-amber',
  critical: 'sk-kpi-icon text-critical bg-criticalSoft',
  info: 'sk-kpi-icon text-info bg-infoSoft',
  neutral: 'sk-kpi-icon text-inkFaint bg-surface2',
};

export interface KpiTrend {
  readonly direction: 'up' | 'down' | 'flat';
  readonly label: string;
}

export interface KpiCardProps {
  readonly label: string;
  readonly value?: string;
  readonly hint?: string;
  readonly soonReason?: string;
  readonly icon?: ReactNode;
  readonly tone?: KpiTone;
  readonly trend?: KpiTrend;
  /** The one figure a row puts forward: its value set larger than its siblings'. */
  readonly featured?: boolean;
}

const TREND_CLASS: Record<KpiTrend['direction'], string> = {
  up: 'text-good',
  down: 'text-critical',
  flat: 'text-inkFaint',
};

export function KpiCard({
  label,
  value,
  hint,
  soonReason,
  icon,
  tone = 'neutral',
  trend,
  featured = false,
}: KpiCardProps): ReactElement {
  const { t } = useI18n();

  // « Souk 2.0 » (Figma): the label, the figure in tabular Manrope, one line under it, and the kind's icon tile in the
  // corner. `featured` sets the value larger instead of filling the card with the brand colour.
  return (
    <Card className="sk-kpi min-h-0 p-4 sm:p-5">
      {icon !== undefined ? (
        <span className={TONE_CLASSES[tone]} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="flex items-center gap-2 pe-11">
        <p className="min-w-0 break-words text-[13px] font-medium leading-snug text-inkFaint">
          {label}
        </p>
      </div>
      {value !== undefined ? (
        // The figure scales with the screen so « 2 465,499 DT » stays on one line; a still longer amount wraps inside
        // its card, never past it.
        <p
          className={`figure mt-4 font-extrabold leading-tight text-ink [overflow-wrap:anywhere] ${
            featured ? 'text-[clamp(1.25rem,4.8vw,2rem)]' : 'text-[clamp(1.1rem,4.2vw,1.625rem)]'
          }`}
        >
          <CountUp value={value} />
        </p>
      ) : null}
      {value !== undefined ? (
        trend !== undefined || hint !== undefined ? (
          // A plain line under the figure, not a boxed pill: a box inside the card read as a card inside a card.
          <p
            className={`mt-1.5 text-xs ${
              trend !== undefined
                ? `font-semibold ${TREND_CLASS[trend.direction]}`
                : 'text-inkFaint'
            }`}
          >
            {trend !== undefined ? (
              <span className="inline-flex items-center gap-1">
                <TrendGlyph direction={trend.direction} />
                {trend.label}
              </span>
            ) : (
              hint
            )}
          </p>
        ) : null
      ) : (
        <div className="mt-4 flex items-center gap-2">
          <Badge tone="neutral">{t('merchant.dashboard.kpiSoonBadge')}</Badge>
          <span className="text-xs text-inkFaint">
            {soonReason ?? t('merchant.dashboard.kpiSoonDefaultReason')}
          </span>
        </div>
      )}
    </Card>
  );
}
